import { Component, computed, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core'
import { BaseListing } from '../_base-listing/base-listing'
import { CoreService } from '../../services'
import { SchemaUtil } from '../../utils'
import { parametersColumns } from '../../utils/definitions.util'
import { injectInfiniteQuery } from '@tanstack/angular-query-experimental'
import { ParameterService } from '../../services/entity-services/parameter.service'

const DEFAULT_FILTERS = {
  name: '',
}

@Component({
  selector: 'app-parameters',
  templateUrl: 'parameters.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ParametersComponent extends BaseListing implements OnInit {
  private parameterService = inject(ParameterService)

  listingColumns = signal(parametersColumns)

  sortProp = signal('name')
  sortDir = signal('asc')

  filters = signal(this.loadViewSettings('parameters')?.filters || structuredClone(DEFAULT_FILTERS))

  canCreate = computed(() => {
    return (
      this.core.services.user.hasRole('ROLE_PARAMETER_CREATE') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
    )
  })

  readonly queryFilters = computed(() => {
    const value = this.filters()
    const f: Record<string, any> = {}
    if (value.name?.length) f['name'] = value.name
    return f
  })

  readonly queryParameters = injectInfiniteQuery(() => ({
    queryKey: ['parameters', { sortProp: this.sortProp(), sortDir: this.sortDir() }],
    queryFn: async ({ pageParam }) => {
      // Persist current view settings before fetching, per tags.component.ts pattern
      return await this.parameterService
        .getParameters(
          {
            page: pageParam,
            sort: this.sortProp(),
            direction: this.sortDir(),
            filters: this.queryFilters(),
          },
          SchemaUtil.parameter
        )
        .then(result => {
          this.saveViewSettings('parameters', {
            filters: this.filters(),
            sortProp: this.sortProp(),
            sortDir: this.sortDir(),
          })
          return result
        })
    },
    initialPageParam: 1,
    getNextPageParam: result =>
      Number(result?.paginator?.current) < Number(result?.paginator?.endPage)
        ? Number(result?.paginator?.current) + 1
        : undefined,
  }))

  totalItems = computed(() => this.queryParameters.data()?.pages?.[0]?.paginator?.totalCount ?? 0)

  constructor(protected core: CoreService) {
    super(core)
    effect(() => {
      this.queryParameters.data()
    })
  }

  ngOnInit() {
    const view = this.loadViewSettings('parameters')
    this.filters.set(view?.filters || structuredClone(DEFAULT_FILTERS))
    this.sortProp.set(view?.sortProp || 'name')
    this.sortDir.set(view?.sortDir || 'asc')
    this.loadSorting('parameters')
  }

  onNameFilterChange(name: string) {
    this.filters.update(v => ({ ...v, name: name ?? '' }))
  }

  clearFilters(): void {
    this.filters.set(structuredClone(DEFAULT_FILTERS))
    this.queryParameters.refetch()
  }
}
